// src/redux/helpSupport/helpSupportSlice.ts

import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  fetchHelpSupportBootstrap,
  createHelpSupportTicket,
  fetchMyHelpSupportTickets,
  fetchHelpSupportTicket,
  replyToHelpSupportTicket,
} from "../../services/helpSupport/helpSupportApi";

import {
  HelpSupportBootstrap,
  HelpSupportTicket,
  CreateHelpSupportTicketPayload,
} from "../../types/helpSupport/helpSupportTypes";


type HelpSupportStatus =
  | "idle"
  | "loading"
  | "succeeded"
  | "failed";


export interface HelpSupportState {
  bootstrap: HelpSupportBootstrap | null;

  tickets: HelpSupportTicket[];
  activeTicket: HelpSupportTicket | null;

  bootstrapStatus: HelpSupportStatus;
  ticketsStatus: HelpSupportStatus;
  ticketStatus: HelpSupportStatus;
  submitStatus: HelpSupportStatus;
  replyStatus: HelpSupportStatus;

  error: string | null;
  successMessage: string | null;
}


const initialState: HelpSupportState = {
  bootstrap: null,

  tickets: [],
  activeTicket: null,

  bootstrapStatus: "idle",
  ticketsStatus: "idle",
  ticketStatus: "idle",
  submitStatus: "idle",
  replyStatus: "idle",

  error: null,
  successMessage: null,
};


// ------------------------------------------------------------
// Error helper
// ------------------------------------------------------------

const resolveErrorMessage = (
  error: any,
  fallback: string,
): string => {
  const data =
    error?.response?.data ??
    error?.data ??
    error;

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (
    typeof data?.detail === "string" &&
    data.detail.trim()
  ) {
    return data.detail;
  }

  if (
    typeof data?.error === "string" &&
    data.error.trim()
  ) {
    return data.error;
  }

  if (
    typeof data?.message === "string" &&
    data.message.trim()
  ) {
    return data.message;
  }

  const firstFieldError = Object.values(
    data ?? {},
  ).find((value) => {
    return (
      typeof value === "string" ||
      Array.isArray(value)
    );
  });

  if (typeof firstFieldError === "string") {
    return firstFieldError;
  }

  if (
    Array.isArray(firstFieldError) &&
    typeof firstFieldError[0] === "string"
  ) {
    return firstFieldError[0];
  }

  return fallback;
};


// ------------------------------------------------------------
// Bootstrap
// ------------------------------------------------------------

export const getHelpSupportBootstrap =
  createAsyncThunk<
    HelpSupportBootstrap,
    void,
    { rejectValue: string }
  >(
    "helpSupport/getBootstrap",
    async (_, { rejectWithValue }) => {
      try {
        return await fetchHelpSupportBootstrap();
      } catch (error: any) {
        return rejectWithValue(
          resolveErrorMessage(
            error,
            "Unable to load Help & Support.",
          ),
        );
      }
    },
  );


// ------------------------------------------------------------
// Create
// ------------------------------------------------------------

export const submitHelpSupportTicket =
  createAsyncThunk<
    {
      message: string;
      data: HelpSupportTicket;
    },
    CreateHelpSupportTicketPayload,
    { rejectValue: string }
  >(
    "helpSupport/submitTicket",
    async (payload, { rejectWithValue }) => {
      try {
        return await createHelpSupportTicket(
          payload,
        );
      } catch (error: any) {
        return rejectWithValue(
          resolveErrorMessage(
            error,
            "Unable to send your request right now.",
          ),
        );
      }
    },
  );


// ------------------------------------------------------------
// Ticket list
// ------------------------------------------------------------

export const getMyHelpSupportTickets =
  createAsyncThunk<
    HelpSupportTicket[],
    void,
    { rejectValue: string }
  >(
    "helpSupport/getMyTickets",
    async (_, { rejectWithValue }) => {
      try {
        return await fetchMyHelpSupportTickets();
      } catch (error: any) {
        return rejectWithValue(
          resolveErrorMessage(
            error,
            "Unable to load your support requests.",
          ),
        );
      }
    },
  );


// ------------------------------------------------------------
// Detail
// ------------------------------------------------------------

export const getHelpSupportTicket =
  createAsyncThunk<
    HelpSupportTicket,
    string,
    { rejectValue: string }
  >(
    "helpSupport/getTicket",
    async (
      publicId,
      { rejectWithValue },
    ) => {
      try {
        return await fetchHelpSupportTicket(
          publicId,
        );
      } catch (error: any) {
        return rejectWithValue(
          resolveErrorMessage(
            error,
            "Unable to load this support request.",
          ),
        );
      }
    },
  );


// ------------------------------------------------------------
// Reply
// ------------------------------------------------------------

export const submitHelpSupportReply =
  createAsyncThunk<
    {
      message: string;
      data: HelpSupportTicket;
    },
    {
      publicId: string;
      message: string;
    },
    { rejectValue: string }
  >(
    "helpSupport/submitReply",
    async (
      {
        publicId,
        message,
      },
      { rejectWithValue },
    ) => {
      try {
        return await replyToHelpSupportTicket(
          publicId,
          {
            message,
          },
        );
      } catch (error: any) {
        return rejectWithValue(
          resolveErrorMessage(
            error,
            "Unable to send your reply.",
          ),
        );
      }
    },
  );


// ------------------------------------------------------------
// Slice
// ------------------------------------------------------------

const helpSupportSlice = createSlice({
  name: "helpSupport",
  initialState,

  reducers: {
    clearHelpSupportMessages(state) {
      state.error = null;
      state.successMessage = null;
    },

    clearActiveHelpSupportTicket(state) {
      state.activeTicket = null;
      state.ticketStatus = "idle";
    },

    setActiveHelpSupportTicket(
      state,
      action: PayloadAction<
        HelpSupportTicket | null
      >,
    ) {
      state.activeTicket = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder

      // Bootstrap
      .addCase(
        getHelpSupportBootstrap.pending,
        (state) => {
          state.bootstrapStatus = "loading";
          state.error = null;
        },
      )
      .addCase(
        getHelpSupportBootstrap.fulfilled,
        (state, action) => {
          state.bootstrapStatus =
            "succeeded";

          state.bootstrap =
            action.payload;
        },
      )
      .addCase(
        getHelpSupportBootstrap.rejected,
        (state, action) => {
          state.bootstrapStatus = "failed";

          state.error =
            action.payload ??
            "Unable to load Help & Support.";
        },
      )

      // Submit
      .addCase(
        submitHelpSupportTicket.pending,
        (state) => {
          state.submitStatus = "loading";
          state.error = null;
          state.successMessage = null;
        },
      )
      .addCase(
        submitHelpSupportTicket.fulfilled,
        (state, action) => {
          state.submitStatus =
            "succeeded";

          state.successMessage =
            action.payload.message ||
            "Your request has been sent to TownLIT Support.";

          state.activeTicket =
            action.payload.data;

          state.tickets = [
            action.payload.data,
            ...state.tickets.filter(
              (ticket) =>
                ticket.public_id !==
                action.payload.data.public_id,
            ),
          ];
        },
      )
      .addCase(
        submitHelpSupportTicket.rejected,
        (state, action) => {
          state.submitStatus = "failed";

          state.error =
            action.payload ??
            "Unable to send your request.";
        },
      )

      // Ticket list
      .addCase(
        getMyHelpSupportTickets.pending,
        (state) => {
          state.ticketsStatus = "loading";
          state.error = null;
        },
      )
      .addCase(
        getMyHelpSupportTickets.fulfilled,
        (state, action) => {
          state.ticketsStatus =
            "succeeded";

          state.tickets =
            action.payload;
        },
      )
      .addCase(
        getMyHelpSupportTickets.rejected,
        (state, action) => {
          state.ticketsStatus = "failed";

          state.error =
            action.payload ??
            "Unable to load support requests.";
        },
      )

      // Detail
      .addCase(
        getHelpSupportTicket.pending,
        (state) => {
          state.ticketStatus = "loading";
          state.error = null;
        },
      )
      .addCase(
        getHelpSupportTicket.fulfilled,
        (state, action) => {
          state.ticketStatus =
            "succeeded";

          state.activeTicket =
            action.payload;
        },
      )
      .addCase(
        getHelpSupportTicket.rejected,
        (state, action) => {
          state.ticketStatus = "failed";

          state.error =
            action.payload ??
            "Unable to load support request.";
        },
      )

      // Reply
      .addCase(
        submitHelpSupportReply.pending,
        (state) => {
          state.replyStatus = "loading";
          state.error = null;
          state.successMessage = null;
        },
      )
      .addCase(
        submitHelpSupportReply.fulfilled,
        (state, action) => {
          state.replyStatus =
            "succeeded";

          state.activeTicket =
            action.payload.data;

          state.successMessage =
            action.payload.message ||
            "Your reply has been sent.";

          state.tickets =
            state.tickets.map(
              (ticket) =>
                ticket.public_id ===
                action.payload.data.public_id
                  ? action.payload.data
                  : ticket,
            );
        },
      )
      .addCase(
        submitHelpSupportReply.rejected,
        (state, action) => {
          state.replyStatus = "failed";

          state.error =
            action.payload ??
            "Unable to send your reply.";
        },
      );
  },
});


export const {
  clearHelpSupportMessages,
  clearActiveHelpSupportTicket,
  setActiveHelpSupportTicket,
} = helpSupportSlice.actions;

export default helpSupportSlice.reducer;